import { Placeholder, Selection } from '@tiptap/extensions'
import escape from 'lodash/escape'
import { VFC, useMemo, useCallback } from 'react'
import { appendStyleOverrides } from '@opallabs/lapidary/src/components/utils'
import { TextEditor } from '../TextEditor'
import { TextEditorContent } from '../TextEditor/content'
import { CharacterCount } from '../TextEditor/extensions/CharacterCount'
import { Normalize } from '../TextEditor/extensions/Normalize'
import { SingleLineDocument } from '../TextEditor/extensions/SingleLineDocument'
import { SocialLinkDecorator } from '../TextEditor/extensions/SocialLinkDecorator'
import { appendNodeTransform } from '../TextEditor/utils'
import { CharacterCountDisplay } from './CharacterCountDisplay'
import * as styles from './styles'
import TruncationHandler from './TruncationHandler'
import { TextEditorInputProps } from './types'

/**
 * `TextEditorInput` is a wrapper for the Tiptap `TextEditor` component.
 * It's specialized for use with plain text in a text input context. It
 * does not allow formatting, and only works with plain text.
 * It includes support for a character counter and content truncation.
 */
export const TextEditorInput: VFC<TextEditorInputProps> = ({
  theme = 'light',
  ariaLabel,
  autofocus,
  initialValue,
  placeholderText,
  readOnly,
  onChange,
  singleLine = false,
  highlightSocialTags = false,
  characterLimit = null,
  truncate = false,
  truncateOptions,
  minRows,
  maxRows,
  rowHeight = 18,
  textAlign = 'start',
  forceLTR = false,
  styleOverrides,
}) => {
  // Tiptap can only accept initialValues as HTML or JSON
  // If provided an initialValue, do a basic plaintext to HTML conversion
  const initialHtmlValue = useMemo(() => {
    // Escape HTML reserved chars
    let htmlValue = escape(initialValue)
    // If multi-line is allowed, replace \n with paragraphs
    // This trick only works if the input has no HTML
    if (!singleLine) {
      htmlValue = htmlValue.replace(/\n/g, '</p><p>')
    }

    return initialValue ? `<p>${htmlValue}</p>` : undefined
  }, [initialValue, singleLine])

  const handleChange = useCallback(
    (content) => {
      // Avoid saving truncated content
      if (!truncate) {
        onChange(content.text)
      }
    },
    [truncate, onChange]
  )

  return (
    <TextEditor
      theme={theme}
      css={[
        styles.editorContainer({
          theme,
          readOnly,
          minRows,
          maxRows,
          rowHeight,
        }),
        ...appendStyleOverrides(styleOverrides?.container),
      ]}
      contentStyle={[
        styles.editorContent({ textAlign }),
        ...appendStyleOverrides(styleOverrides?.content),
      ]}
      extensions={{
        // StarterKit extensions
        blockquote: { config: false },
        bold: { config: false },
        bulletList: { config: false },
        code: { config: false },
        codeBlock: { config: false },
        document: { config: !singleLine },
        dropcursor: { config: false },
        gapcursor: { config: false },
        hardBreak: { config: false },
        heading: { config: false },
        horizontalRule: { config: false },
        italic: { config: false },
        link: { config: false },
        listKeymap: { config: false },
        listItem: { config: false },
        orderedList: { config: false },
        paragraph: { config: true },
        strike: { config: false },
        text: { config: true },
        trailingNode: { config: false },
        underline: { config: false },
        undoRedo: { config: true },
        // Imported extensions
        characterCount: {
          extension: CharacterCount,
        },
        normalize: {
          extension: Normalize,
        },
        placeholder: {
          extension: Placeholder,
          config: { showOnlyWhenEditable: true },
        },
        selection: {
          extension: Selection,
        },
        singleLineDocument: {
          extension: SingleLineDocument,
          config: singleLine,
        },
        socialLinkDecorator: {
          extension: SocialLinkDecorator,
          config: highlightSocialTags,
        },
      }}
      ariaLabel={ariaLabel}
      dir={forceLTR ? 'ltr' : 'auto'}
      autofocus={autofocus}
      placeholderText={placeholderText}
      characterLimit={characterLimit}
      readOnly={readOnly}
      initialValue={initialHtmlValue}
      onSaveContent={handleChange}
      saveOnChange={{ delay: 0 }}
      editorPropsOverrides={{
        transformPasted: (slice, view) =>
          // Appends an extra blank paragraph to each non-empty
          // paragraph in pasted content, to visually match the
          // behavior of Kuiper TextEditorInput
          appendNodeTransform(slice, (node, index, fragment) => {
            const next = fragment.maybeChild(index + 1)

            return node.type.name === 'paragraph' &&
              node.content.size > 0 &&
              next &&
              next.content.size > 0
              ? view.state.schema.node('paragraph')
              : null
          }),
      }}
    >
      <TextEditorContent />
      <TruncationHandler
        truncate={truncate}
        truncateOptions={truncateOptions}
      />
      {characterLimit != null && characterLimit.limit != null && (
        <CharacterCountDisplay
          {...characterLimit}
          limit={characterLimit.limit}
        />
      )}
    </TextEditor>
  )
}
